<template>
  <v-card
    class="mx-auto mb-4 glass-card"
    max-width="380"
    outlined
    rounded="xl"
    elevation="0"
    @click="$emit('view', profile)"
  >
    <!-- Header -->
    <v-card-title
      class="d-flex align-center justify-space-between pa-2"
      :style="{ backgroundColor: headerColor }"
    >
      <v-list-item>
        <v-list-item-avatar color="grey lighten-3" class="relative">
          <v-img
            :src="getDefaultImage(profile.gender)"
            alt="avatar"
            class="elevation-3"
          />
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title class="font-weight-bold text-h6">
            {{ profile.name }}
          </v-list-item-title>
        </v-list-item-content>

        <v-list-item-action-text>
          <v-chip
            v-if="profile.dob"
            small
            color="accent"
            dark
            class="font-weight-medium"
          >
            {{ calculateAge(profile.dob) }} yrs
          </v-chip>
        </v-list-item-action-text>
      </v-list-item>
    </v-card-title>

    <!-- Body -->
    <v-card-text class="mt-5">
      <div
        v-if="profile.Creator?.name || profile.guardianName"
        class="d-flex align-center mb-3"
      >
        <span class="font-weight-medium">
          Guardian:
          <strong>{{
            (profile.Creator && profile.Creator.name) || profile.guardianName
          }}</strong>
        </span>
        
        <UserOnlineStatus
          v-if="profile?.Creator?.id"
          class="ml-2"
          :userId="profile.Creator.id"
        />
      </div>

      <div class="text-body-2 mb-2">
        {{ profile.bio || "No bio available." }}
      </div>

      <div class="grey--text text--darken-1 mb-2">
        {{ profile.state }}, {{ profile.country }}
      </div>

      <v-chip-group column>
        <v-chip
          v-if="profile.maritalStatus"
          color="pink lighten-4"
          small
          label
          text-color="pink darken-5"
          class="ma-1 font-weight-bold"
        >
          {{ profile.maritalStatus }}
        </v-chip>
        <v-chip
          v-if="profile.madhab"
          color="light-blue lighten-4"
          label
          small
          text-color="light-blue darken-5"
          class="ma-1 font-weight-bold"
        >
          {{ profile.madhab }}
        </v-chip>
        <v-chip
          v-if="profile.educationLevel"
          color="deep-purple lighten-4"
          label
          small
          text-color="deep-purple darken-5"
          class="ma-1 font-weight-bold"
        >
          {{ profile.educationLevel }}
        </v-chip>
        <v-chip
          v-if="profile.ethnicity"
          color="teal lighten-4"
          text-color="teal darken-5"
          class="ma-1 font-weight-bold"
          label
          small
        >
          {{ profile.ethnicity }}
        </v-chip>
      </v-chip-group>

      <!-- 🟢 Match Score -->
      <div v-if="profile.matchScore !== undefined" class="mt-4">
        <v-progress-linear
          :value="profile.matchScore"
          height="6"
          rounded
          :color="
            profile.matchScore >= 80
              ? 'green'
              : profile.matchScore >= 50
              ? 'orange'
              : 'red'
          "
        ></v-progress-linear>
        <div
          class="text-caption text-center mt-1"
          :class="{
            'green--text': profile.matchScore >= 80,
            'orange--text': profile.matchScore >= 50 && profile.matchScore < 80,
            'red--text': profile.matchScore < 50,
          }"
        >
          Match Compatibility: {{ profile.matchScore }}%
        </div>
      </div>
    </v-card-text>

    <!-- Footer -->
    <v-card-actions
      class="pa-3 pt-2 pb-3 d-flex align-center justify-space-between"
    >
      <!-- Left: Icons -->
      <div class="d-flex align-center flex-wrap">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-if="profile.canDrive === 'yes'"
              color="teal darken-2"
              class="mr-2"
              small
              v-bind="attrs"
              v-on="on"
            >
              mdi-car
            </v-icon>
          </template>
          <span>Can Drive</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-if="profile.givesZakat === 'yes'"
              color="amber darken-2"
              class="mr-2"
              small
              v-bind="attrs"
              v-on="on"
            >
              mdi-hand-heart
            </v-icon>
          </template>
          <span>Gives Zakat</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-if="profile.smokes === 'yes'"
              color="red darken-1"
              class="mr-2"
              small
              v-bind="attrs"
              v-on="on"
            >
              mdi-smoking
            </v-icon>
            <v-icon
              v-else-if="profile.smokes === 'no'"
              color="green darken-2"
              class="mr-2"
              small
              v-bind="attrs"
              v-on="on"
            >
              mdi-smoking-off
            </v-icon>
          </template>
          <span>
            {{
              profile.smokes === "yes"
                ? "Smokes"
                : profile.smokes === "no"
                ? "Non-Smoker"
                : "Smoking status not specified"
            }}
          </span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-if="profile.drinks === 'yes'"
              color="deep-purple darken-1"
              class="mr-2"
              small
              v-bind="attrs"
              v-on="on"
            >
              mdi-glass-wine
            </v-icon>
            <v-icon
              v-else-if="profile.drinks === 'no'"
              color="blue darken-3"
              class="mr-2"
              small
              v-bind="attrs"
              v-on="on"
            >
              mdi-cup-water
            </v-icon>
          </template>
          <span>
            {{
              profile.drinks === "yes"
                ? "Drinks Alcohol"
                : profile.drinks === "no"
                ? "Non-Drinker"
                : "Drinking status not specified"
            }}
          </span>
        </v-tooltip>
      </div>

      <!-- Right: Likes & Views -->
      <div class="d-flex align-center">
        <v-icon color="pink" small class="mr-1">mdi-heart</v-icon>
        <span class="font-weight-medium mr-3">{{
          profile.likeCount || 0
        }}</span>

        <v-icon color="blue darken-2" small class="mr-1">mdi-eye</v-icon>
        <span class="font-weight-medium">{{ profile.viewCount || 0 }}</span>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
import UserOnlineStatus from "@/components/UserOnlineStatus.vue";

export default {
  props: {
    profile: Object,
    onlineUserIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      headerColor: this.getRandomColor(),
    };
  },
  components: { UserOnlineStatus },
  methods: {
    getRandomColor() {
      const colors = [
        "#81D4FA",
        "#B2EBF2",
        "#FFECB3",
        "#C8E6C9",
        "#F8BBD0",
        "#D1C4E9",
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },
    calculateAge(dob) {
      if (!dob) return null;
      const birth = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      return age;
    },
    getDefaultImage(gender) {
      return gender?.toLowerCase() === "female"
        ? require("@/assets/avatars/wife.png")
        : require("@/assets/avatars/brother.png");
    },
  },
};
</script>

<style scoped>
/* Keep your existing glass-card styles here */
.glass-card {
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  overflow: hidden;
}
/* Accent strip */
.glass-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  border-radius: 20px 20px 0 0;
  background: linear-gradient(
    90deg,
    var(--accent-color, #64b5f6),
    rgba(255, 255, 255, 0.4)
  );
}
.glass-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}
</style>